/**
 * The console's recorder: the browser's microphone, through `MediaRecorder`,
 * into `dictation-api.ts`'s upload and poll.
 *
 * Click to start, click to stop. The phone is press-and-hold because it is
 * used one-handed on site; a desk has a mouse, and holding a button down for
 * minutes is not something a mouse does well.
 *
 * The recording stays in memory until the transcript lands, so every failure
 * after the microphone can offer *Try again* on the same audio — and a
 * download, because a browser tab is the only copy until the upload finishes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DICTATION_AUDIO_TYPES,
  atRecordingCap,
} from "@gb/contracts";
import {
  DIDNT_CATCH,
  DictationError,
  pollDictation,
  uploadDictation,
  type DictationResult,
} from "./dictation-api";

export type DictationPhase =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "recording"; elapsedSeconds: number }
  | { kind: "sending" }
  | { kind: "transcribing" }
  | {
      kind: "failed";
      message: string;
      /** Trying the same thing again might work. */
      retryable: boolean;
      /** The audio is still in this window, so it can be re-sent or saved. */
      hasRecording: boolean;
    };

export const NO_RECORDER =
  "This browser can’t record audio here — type the note instead.";
export const MIC_BLOCKED =
  "The microphone is blocked for this site. Allow it from the address bar, then try again — or type the note.";
export const NO_MIC = "No microphone was found — type the note instead.";
export const MIC_NO_ANSWER =
  "The microphone didn’t answer — it may be in use by another app. Try again, or type the note.";
export const NOTHING_RECORDED =
  "Nothing was recorded — try again, or type the note.";
export const FORMAT_REFUSED =
  "This browser records audio in a format GroundBrain can’t take — type the note instead.";

/**
 * The formats asked for, in order. Each is one the API accepts
 * (`DICTATION_AUDIO_TYPES`); Chrome and Firefox take the first, Safari only
 * records MP4. What the recorder actually produced is read back from it
 * afterwards rather than assumed from what was asked.
 */
const PREFERRED_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];

/** Often enough that the clock never visibly skips a second. */
const TICK_MS = 250;
/** How often the recorder hands over what it has, so a crash loses little. */
const SLICE_MS = 1_000;

type Recording = { audio: Blob; mimeType: string; extension: string; durationSeconds: number };

export function useDictation(input: {
  projectId: string;
  /** The date the composer is filing for, read when the recording is sent. */
  date: string;
  onResult: (result: DictationResult) => void;
}) {
  const [phase, setPhase] = useState<DictationPhase>({ kind: "idle" });

  // Read at send time, not captured at start — the date can change while
  // someone is talking, and the callback closes over the composer's state.
  const latest = useRef(input);
  latest.current = input;

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);
  const recording = useRef<Recording | null>(null);
  /** Set once the bytes are stored; a retry then polls instead of re-sending. */
  const dictationId = useRef<string | null>(null);
  /** Bumped to cancel whatever flow is running — a close, a discard, a new take. */
  const generation = useRef(0);

  const release = useCallback(() => {
    if (ticker.current) clearInterval(ticker.current);
    ticker.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  const fail = useCallback((error: unknown) => {
    const known = error instanceof DictationError;
    if (!known) console.warn("Dictation failed:", error);
    setPhase({
      kind: "failed",
      message: known ? error.message : DIDNT_CATCH,
      retryable: known ? error.retryable : false,
      hasRecording: recording.current !== null,
    });
  }, []);

  const send = useCallback(async () => {
    const take = recording.current;
    if (!take) return;
    const mine = ++generation.current;
    const stale = () => generation.current !== mine;
    const { projectId } = latest.current;
    try {
      if (!dictationId.current) {
        setPhase({ kind: "sending" });
        const id = await uploadDictation({ projectId, date: latest.current.date, ...take });
        if (stale()) return;
        dictationId.current = id;
      }
      setPhase({ kind: "transcribing" });
      const result = await pollDictation(projectId, dictationId.current, { cancelled: stale });
      if (!result || stale()) return;
      // Settled with nothing heard. The audio is stored and joined to
      // nothing; saying so beats closing the dialog on an unchanged note.
      if (result.transcript.trim().length === 0) throw new DictationError(DIDNT_CATCH, false);
      recording.current = null;
      dictationId.current = null;
      setPhase({ kind: "idle" });
      latest.current.onResult(result);
    } catch (error) {
      if (!stale()) fail(error);
    }
  }, [fail]);

  const start = useCallback(async () => {
    if (
      typeof MediaRecorder === "undefined" ||
      typeof navigator.mediaDevices?.getUserMedia !== "function"
    ) {
      setPhase({ kind: "failed", message: NO_RECORDER, retryable: false, hasRecording: false });
      return;
    }
    const mine = ++generation.current;
    recording.current = null;
    dictationId.current = null;
    setPhase({ kind: "starting" });

    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      if (generation.current !== mine) return;
      const name = error instanceof DOMException ? error.name : "";
      const message =
        name === "NotAllowedError" || name === "SecurityError"
          ? MIC_BLOCKED
          : name === "NotFoundError" || name === "OverconstrainedError"
            ? NO_MIC
            : MIC_NO_ANSWER;
      setPhase({ kind: "failed", message, retryable: message !== NO_MIC, hasRecording: false });
      return;
    }
    // Closed while the browser was asking for permission.
    if (generation.current !== mine) {
      media.getTracks().forEach((track) => track.stop());
      return;
    }
    stream.current = media;

    const asked = PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    const rec = new MediaRecorder(media, asked ? { mimeType: asked } : undefined);
    const mimeType = rec.mimeType || asked || "";
    const extension = DICTATION_AUDIO_TYPES[(mimeType.split(";")[0] ?? "").trim().toLowerCase()]?.[0];
    if (!extension) {
      release();
      setPhase({ kind: "failed", message: FORMAT_REFUSED, retryable: false, hasRecording: false });
      return;
    }

    const chunks: Blob[] = [];
    const startedAt = Date.now();
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    rec.onstop = () => {
      release();
      recorder.current = null;
      // A discard or a close bumped the generation: nothing to send.
      if (generation.current !== mine) return;
      const audio = new Blob(chunks, { type: mimeType });
      if (audio.size === 0) {
        setPhase({ kind: "failed", message: NOTHING_RECORDED, retryable: true, hasRecording: false });
        return;
      }
      recording.current = {
        audio,
        mimeType,
        extension,
        durationSeconds: (Date.now() - startedAt) / 1000,
      };
      void send();
    };

    recorder.current = rec;
    rec.start(SLICE_MS);
    setPhase({ kind: "recording", elapsedSeconds: 0 });
    // The cap is enforced here as well as on the server: at ten minutes the
    // recorder stops itself and sends what it has (`architecture/0019` §7).
    ticker.current = setInterval(() => {
      const elapsedSeconds = (Date.now() - startedAt) / 1000;
      if (atRecordingCap(elapsedSeconds)) {
        if (rec.state === "recording") rec.stop();
        return;
      }
      setPhase({ kind: "recording", elapsedSeconds });
    }, TICK_MS);
  }, [release, send]);

  /** Stop talking and send it. */
  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  /** Throw the take away — the one path that drops audio, and only on a click. */
  const discard = useCallback(() => {
    generation.current++;
    if (recorder.current?.state === "recording") recorder.current.stop();
    release();
    recording.current = null;
    dictationId.current = null;
    setPhase({ kind: "idle" });
  }, [release]);

  /** The same audio again: re-polled if it was stored, re-sent if not. */
  const retry = useCallback(() => {
    if (recording.current) void send();
    else void start();
  }, [send, start]);

  /** Save the take to disk — the browser holds the only copy until it is stored. */
  const download = useCallback(() => {
    const take = recording.current;
    if (!take) return;
    const url = URL.createObjectURL(take.audio);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dictation-${latest.current.date}.${take.extension}`;
    link.click();
    // Not revoked at once: some browsers start the download after the click returns.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, []);

  // The composer closing ends everything: no recording left running, no poll
  // left asking, no microphone light left on.
  useEffect(
    () => () => {
      generation.current++;
      if (recorder.current?.state === "recording") recorder.current.stop();
      release();
    },
    [release],
  );

  return { phase, start, stop, discard, retry, download };
}
